import React, { useState, useEffect, useCallback } from 'react';
import { logError } from '@/lib/logger';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { ArrowLeft, Check, Sparkles } from 'lucide-react';
import {
  StepIndicator,
  OnboardingHeader,
  OptionCard,
  MultiSelectOption,
  ATTACHMENT_OPTIONS,
  HEALING_FOCUS_OPTIONS,
  NERVOUS_SYSTEM_OPTIONS
} from '@/components/onboarding';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const OnboardingPage = () => {
  const navigate = useNavigate();
  const { user, token, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(true);
  
  // Form data
  const [attachmentStyle, setAttachmentStyle] = useState('');
  const [healingFocus, setHealingFocus] = useState([]);
  const [nervousSystemResponse, setNervousSystemResponse] = useState('');

  // Check if onboarding already complete and restore progress
  useEffect(() => {
    const fetchProgress = async () => {
      if (user?.onboarding_complete) {
        navigate('/sanctuary');
        return;
      }
      
      try {
        const response = await axios.get(`${API}/onboarding/progress`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const progress = response.data;
        if (progress.attachment_style) setAttachmentStyle(progress.attachment_style);
        if (progress.healing_focus) setHealingFocus(progress.healing_focus);
        if (progress.nervous_system_response) setNervousSystemResponse(progress.nervous_system_response);
        if (progress.current_step && progress.current_step > 1) {
          setCurrentStep(Math.min(progress.current_step, 5));
        }
      } catch (error) {
        // Progress fetch failed - start fresh (non-critical)
                  logError('Onboarding progress fetch failed:', error?.response?.status);
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchProgress();
  }, [user, token, navigate]);

  // Save progress when moving between steps
  const saveProgress = useCallback(async (step, data = {}) => {
    try {
      await axios.post(
        `${API}/onboarding/progress`,
        {
          current_step: step,
          attachment_style: data.attachment_style || attachmentStyle || null,
          healing_focus: data.healing_focus || (healingFocus.length > 0 ? healingFocus : null),
          nervous_system_response: data.nervous_system_response || nervousSystemResponse || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      // Progress save failure is non-critical - user can continue
              logError('Onboarding progress save failed:', error?.response?.status);
    }
  }, [token, attachmentStyle, healingFocus, nervousSystemResponse]);

  const goToStep = useCallback((step) => {
    setFadeIn(false);
    setTimeout(() => {
      setCurrentStep(step);
      setFadeIn(true);
    }, 300);
  }, []);

  const handleBack = useCallback(() => {
    if (currentStep > 1) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  const handleNext = useCallback(() => {
    if (currentStep === 2 && !attachmentStyle) {
      toast.error('Please select an option to continue');
      return;
    }
    if (currentStep === 3 && healingFocus.length === 0) {
      toast.error('Please select at least one focus area');
      return;
    }
    if (currentStep === 4 && !nervousSystemResponse) {
      toast.error('Please select an option to continue');
      return;
    }

    if (currentStep < 5) {
      const nextStep = currentStep + 1;
      saveProgress(nextStep);
      goToStep(nextStep);
    }
  }, [currentStep, attachmentStyle, healingFocus, nervousSystemResponse, saveProgress, goToStep]);

  const handleComplete = useCallback(async () => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/onboarding/complete`,
        {
          attachment_style: attachmentStyle,
          healing_focus: healingFocus,
          nervous_system_response: nervousSystemResponse
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser();
      navigate('/sanctuary');
    } catch {
      toast.error('Failed to save your preferences');
    } finally {
      setLoading(false);
    }
  }, [attachmentStyle, healingFocus, nervousSystemResponse, token, refreshUser, navigate]);

  const toggleHealingFocus = useCallback((focus) => {
    setHealingFocus(prev => 
      prev.includes(focus) 
        ? prev.filter(f => f !== focus)
        : [...prev, focus]
    );
  }, []);

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FBF9F6' }}>
        <div className="animate-pulse text-[#6B6878]">Loading...</div>
      </div>
    );
  }

  const preferredName = user?.preferred_name || user?.name || 'there';

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#FBF9F6' }}
    >
      <div className="flex-1 px-6 py-12 max-w-2xl mx-auto w-full">
        {/* Progress indicator */}
        <div className="w-full max-w-md mx-auto mb-8">
          <div className="flex justify-between mb-2">
            {[1, 2, 3, 4, 5].map((step) => {
              const getStepClass = () => {
                if (step < currentStep) return 'bg-[#C9A84C] text-[#FBF9F6]';
                if (step === currentStep) return 'bg-[#C9A84C]/20 text-[#C9A84C] border border-[#C9A84C]';
                return 'bg-[#F7F1EE] text-[#A3A0AE]';
              };
              return (
                <div
                  key={step}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-300 ${getStepClass()}`}
                >
                  {step < currentStep ? <Check className="w-4 h-4" /> : step}
                </div>
              );
            })}
          </div>
          <div className="h-1 bg-[#F7F1EE] rounded-full overflow-hidden">
            <div 
              className="h-full bg-[#C9A84C] transition-all duration-500 ease-out"
              style={{ width: `${((currentStep - 1) / 4) * 100}%` }}
            />
          </div>
        </div>

        {/* Back button */}
        {currentStep > 1 && currentStep < 5 && (
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-[#6B6878] hover:text-[#1A1A2E] mb-6 transition-colors"
            data-testid="onboarding-back-button"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </button>
        )}

        {/* Step content */}
        <div className={`transition-all duration-300 ${fadeIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {/* Step 1: Welcome */}
          {currentStep === 1 && (
            <div className="text-center relative">
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
                <div 
                  className="w-96 h-96 rounded-full opacity-20 blur-3xl animate-pulse"
                  style={{ background: 'radial-gradient(circle, #C9A84C 0%, transparent 70%)' }}
                />
              </div>
              <div className="relative z-10">
                <Sparkles className="w-12 h-12 mx-auto mb-6" style={{ color: '#C9A84C' }} />
                <h1 className="text-3xl md:text-4xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}>
                  Let's personalize your healing journey
                </h1>
                <p className="text-lg mb-12 max-w-md mx-auto" style={{ color: '#4A4A5A' }}>
                  Sage will use your answers to show up for you in the way you need most. There are no wrong answers here.
                </p>
                <Button
                  onClick={handleNext}
                  className="px-12 py-6 text-lg rounded-full font-medium"
                  style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
                  data-testid="onboarding-begin-button"
                >
                  I'm Ready — Let's Begin
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Attachment Style */}
          {currentStep === 2 && (
            <div>
              <h1 className="text-2xl md:text-3xl font-medium mb-2 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}>
                How do you show up in relationships?
              </h1>
              <p className="text-center mb-8" style={{ color: '#4A4A5A' }}>
                Choose the one that feels most familiar
              </p>
              <div className="space-y-4 max-w-lg mx-auto">
                {ATTACHMENT_OPTIONS.map((option) => (
                  <OptionCard
                    key={option.id}
                    selected={attachmentStyle === option.id}
                    onClick={() => setAttachmentStyle(option.id)}
                    label={option.label}
                    description={option.description}
                    color={option.color}
                    testId={`attachment-${option.id}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Healing Focus */}
          {currentStep === 3 && (
            <div>
              <h1 className="text-2xl md:text-3xl font-medium mb-2 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}>
                What are you healing from?
              </h1>
              <p className="text-center mb-8" style={{ color: '#4A4A5A' }}>
                Select all that resonate (you can choose multiple)
              </p>
              <div className="grid grid-cols-1 gap-3 max-w-lg mx-auto">
                {HEALING_FOCUS_OPTIONS.map((focus) => (
                  <MultiSelectOption
                    key={focus}
                    selected={healingFocus.includes(focus)}
                    onClick={() => toggleHealingFocus(focus)}
                    label={focus}
                    testId={`healing-${focus.toLowerCase().replace(/\s+/g, '-')}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Nervous System Response */}
          {currentStep === 4 && (
            <div>
              <h1 className="text-2xl md:text-3xl font-medium mb-2 text-center" style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}>
                When you're triggered, what do you do?
              </h1>
              <p className="text-center mb-8" style={{ color: '#4A4A5A' }}>
                Choose your most common response
              </p>
              <div className="space-y-4 max-w-lg mx-auto">
                {NERVOUS_SYSTEM_OPTIONS.map((option) => (
                  <OptionCard
                    key={option.id}
                    selected={nervousSystemResponse === option.id}
                    onClick={() => setNervousSystemResponse(option.id)}
                    label={option.label}
                    description={option.description}
                    color={option.color}
                    testId={`nervous-${option.id}`}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Step 5: Completion */}
          {currentStep === 5 && (
            <div className="text-center">
              <div 
                className="w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, rgba(232,180,184,0.2), rgba(201,168,76,0.2))' }}
              >
                <Sparkles className="w-10 h-10" style={{ color: '#C9A84C' }} />
              </div>
              <h1 className="text-2xl md:text-3xl font-medium mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#C9A84C' }}>
                You're all set, {preferredName}
              </h1>
              <p className="text-lg mb-12 max-w-md mx-auto" style={{ color: '#4A4A5A' }}>
                Sage is ready to meet you exactly where you are. Your healing journey begins now.
              </p>
              <Button
                onClick={handleComplete}
                disabled={loading}
                className="px-12 py-6 text-lg rounded-full font-medium"
                style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
                data-testid="onboarding-complete-button"
              >
                {loading ? 'Saving...' : 'Enter Your Sanctuary'}
              </Button>
            </div>
          )}
        </div>

        {/* Continue button (steps 2-4) */}
        {currentStep >= 2 && currentStep <= 4 && (
          <div className="mt-12 text-center">
            <Button
              onClick={handleNext}
              className="px-12 py-6 text-lg rounded-full font-medium"
              style={{ backgroundColor: '#C9A84C', color: '#FBF9F6' }}
              data-testid="onboarding-continue-button"
            >
              Continue
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
